/**
 * Sound service for notification and message audio feedback.
 *
 * Plays synthesized sounds using the Web Audio API.
 * Respects user preference stored in localStorage.
 */

const STORAGE_KEY = "ano-sounds-enabled";

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

/**
 * Ensure the AudioContext is running before scheduling nodes.
 * `resume()` is a no-op when already running; otherwise it returns
 * a promise that resolves once the context is unblocked.
 */
async function ensureRunning(ctx: AudioContext): Promise<void> {
  if (ctx.state !== "running") {
    await ctx.resume();
  }
}

export function isSoundEnabled(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY);
  // Default: enabled
  return stored !== "false";
}

export function setSoundEnabled(enabled: boolean) {
  localStorage.setItem(STORAGE_KEY, String(enabled));
}

/**
 * Short two-tone chime for new messages / notifications.
 */
function playNotificationSound(ctx: AudioContext) {
  const now = ctx.currentTime;

  // Tone 1
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = "sine";
  osc1.frequency.value = 880; // A5
  gain1.gain.setValueAtTime(0.15, now);
  gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  osc1.connect(gain1).connect(ctx.destination);
  osc1.start(now);
  osc1.stop(now + 0.15);

  // Tone 2 (higher, slightly delayed)
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = "sine";
  osc2.frequency.value = 1175; // D6
  gain2.gain.setValueAtTime(0.12, now + 0.08);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
  osc2.connect(gain2).connect(ctx.destination);
  osc2.start(now + 0.08);
  osc2.stop(now + 0.25);
}

/**
 * Soft single tone for mentions.
 */
function playMentionSound(ctx: AudioContext) {
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(1320, now); // E6
  osc.frequency.exponentialRampToValueAtTime(880, now + 0.2);
  gain.gain.setValueAtTime(0.18, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.3);
}

/**
 * Looping ring tone for incoming calls.
 * Two sine oscillators at 440 Hz + 480 Hz (US ring cadence).
 * Returns a `stop` function to silence and clean up.
 */
export function startRinging(): () => void {
  if (!isSoundEnabled()) return () => {};

  const ctx = getAudioContext();
  let stopped = false;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  const activeNodes: { osc: OscillatorNode; gain: GainNode }[] = [];

  function playBurst() {
    if (stopped) return;
    const now = ctx.currentTime;

    // First burst at t=0, second burst at t=0.6
    for (const offset of [0, 0.6]) {
      for (const freq of [440, 480]) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.12, now + offset);
        gain.gain.setValueAtTime(0.12, now + offset + 0.35);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + offset);
        osc.stop(now + offset + 0.4);
        activeNodes.push({ osc, gain });
        osc.onended = () => {
          const idx = activeNodes.findIndex((n) => n.osc === osc);
          if (idx !== -1) activeNodes.splice(idx, 1);
        };
      }
    }
  }

  void ensureRunning(ctx)
    .then(() => {
      if (stopped) return;
      playBurst();
      intervalId = setInterval(playBurst, 3000);
    })
    .catch(() => {});

  return () => {
    stopped = true;
    if (intervalId !== null) clearInterval(intervalId);
    for (const { osc } of activeNodes) {
      try {
        osc.stop();
      } catch {
        // already stopped
      }
    }
    activeNodes.length = 0;
  };
}

export type SoundType = "notification" | "mention";

/**
 * Play a sound by type. No-op if sounds are disabled.
 */
export function playSound(type: SoundType) {
  if (!isSoundEnabled()) return;

  const ctx = getAudioContext();

  // Schedule nodes only after the context is confirmed running so
  // currentTime is advancing and scheduled times are in the future.
  void ensureRunning(ctx)
    .then(() => {
      switch (type) {
        case "notification":
          playNotificationSound(ctx);
          break;
        case "mention":
          playMentionSound(ctx);
          break;
      }
    })
    .catch(() => {
      // Audio context blocked by browser policy — silently ignore
    });
}
